// @flow
// -----------------------------------------------------------------
// <copyright
//   file="Izenda.ConnectionStringBuilderRest.js"
//   company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// -----------------------------------------------------------------

import connect from 'share/enhancers/Izenda.Connect';
import { action, observable } from 'mobx';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  getOr,
  filter,
  first,
  get,
  find,
  flatten,
  flow,
  isArray,
  isEmpty,
  isNil,
  each,
  map,
  partial,
  toPairs,
  some
} from 'lodash/fp';
import classnames from 'classnames';

import localizer from 'share/localization/Izenda.Localizer';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import Button from 'share/components/controls/Izenda.Button';

import ConnectionStringBuilderEndpoint from './Izenda.ConnectionStringBuilderEndpoint';
import ConnectionRestEndpoint from 'logic/models/Izenda.ConnectionRestEndpoint';
import CachingConfigurationStore from 'logic/Izenda.CachingConfigurationStore';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import BrowserUtils from "share/utils/Izenda.BrowserUtils";

const LoadingComponent = () => (
  <div style={{ width: '100%' }}>
    <ScrollBox>{ReportPartUtils.renderLazyLoadingBar()}</ScrollBox>
  </div>
);

@connect()
export default class ConnectionStringBuilderRest extends Component {

  static propTypes = {
    connectionDetailStore: PropTypes.any,
    endpointModels: PropTypes.any // Array and ObservableArray
  };

  cachingConfiguration = new CachingConfigurationStore();
  state = {
    isLoaded: false,
    isAdvancedVisible: false,
    currentEndpoint: this.createNewEndpointInstance(),
    presets: []
  };

  /**
   * Endpoint models for current connection
   * @type {ConnectionRestEndpoint[]}
   */
  @observable endpointModels = [];

  componentDidMount() {
    const { props } = this;
    Promise
      .all([
        this.loadPresets(),
        this.loadConnectionStringData(),
        this.cachingConfiguration.loadCachingConfiguration()
      ])
      .then(
        results => {
          let currentEndpoint;
          if (props.endpointModels && props.endpointModels.length) {
            this.endpointModels = props.endpointModels;
            currentEndpoint = this.endpointModels[0];
          } else {
            const newEndpoint = this.createNewEndpointInstance();
            newEndpoint.endpointName = this.getNewEndpointName();
            this.endpointModels.push(newEndpoint);
            currentEndpoint = newEndpoint
          }
          this.state = {
            isAdvancedVisible: false,
            currentEndpoint: currentEndpoint
          };

          // Restore from preset
          const connectionStringData = results[1];
          const endpointsToRestore = get('[1]', first(connectionStringData));
          this.onRestoreFromPresets(endpointsToRestore);

          // Set up other presets
          const presets = results[0];
          this.setState({ presets: presets || [] });
        },
        error => {
          props.confirmMessage('error', getOr(
            localizer.getText('COMMON_MESSAGES_UNKNOWN_ERROR'),
            'messages[0].messages[0]',
            error));
        }
      ).finally(() => this.setState({ isLoaded: true }));
  }

  /**
   * Create new endpoint instance
   * @returns {ConnectionRestEndpoint}
   */
  createNewEndpointInstance() {
    const { isEnableDataCache, timeToLive, defaultTimeToLive, defaultIsEnableDataCache } =
      this.cachingConfiguration.cachingConfiguration.dataCachingConfiguration;
    return new ConnectionRestEndpoint(isEnableDataCache, timeToLive, defaultIsEnableDataCache, defaultTimeToLive);
  }

  /**
   * Perform connection
   * @param {string} connectionString connection string
   * @returns {Promise<object>} API call result object.
   */
  performConnection(connectionString) {
    const { connectionDetailStore } = this.props;
    return new Promise(resolve => {
      connectionDetailStore
        .performConnection(connectionString)
        .then(result => {
          resolve(result);
          return result;
        });
    });
  }

  /**
   * Close current popup.
   */
  closePopup = (callBack, type) => {
    const { props } = this;
    const { endpointModels } = this;
    const { validation, onAccept, onReject } = props;
    if (type === 'accept') {
      // form validation
      validation
        .validate()
        .then(success => {
          if (!success) {
            return;
          }
          const firstInvalidEndpoint = find(endpoint => !!endpoint.getValidationMessage(endpointModels), endpointModels);
          if (!isNil(firstInvalidEndpoint)) {
            return;
          }
          const connectionString = JSON.stringify(this.composeConnectionString());
          // perform connection, if everything is okay - close popup
          this.setState({
            isLoaded: false
          });
          this
            .performConnection(connectionString)
            .then(result => {
              if (result.success) {
                onAccept && onAccept({
                  connectionString: connectionString,
                  endpointModels: this.endpointModels
                });
                return null;
              }
              this.setState({
                isLoaded: true
              });
              const errorText = getOr(
                localizer.getText('COMMON_MESSAGES_GENERIC_ERROR'),
                'messages[0].messages[0]',
                result);
              props.confirmMessage('error', errorText);

            }, error => {
              this.setState({
                isLoaded: true
              });
              console.error('Unknown error', error);
              const errorText = getOr(
                localizer.getText('COMMON_MESSAGES_GENERIC_ERROR'),
                'message',
                result);
              props.confirmMessage('error', errorText);
            });

        });
    } else {
      onReject && onReject();
    }
  };

  /**
   * Compose connection string from parts.
   * @returns {string} Rest connection string
   */
  composeConnectionString(): string {
    const { endpointModels } = this;
    const result = {
      endpoints: []
    };
    each(endpoint => {
      result.endpoints.push(endpoint.composeConnectionString());
    }, endpointModels);
    return result;
  }

  /**
   * Update presets collection
   */
  getUnsavedPresets() {
    const { currentEndpoint } = this.state;
    return flow(
      filter(x => x !== currentEndpoint),
      map(x => x.toPreset())
    )(this.endpointModels);
  }

  /**
   * Restore previous connection details from set presents
   * @param {Array} presets
   */
  onRestoreFromPresets = presets => {
    if (isEmpty(presets))
      return;
    this.endpointModels = map(preset => {
      const endpoint = this.createNewEndpointInstance();
      endpoint.applyPreset(preset, true);
      return endpoint;
    }, presets);

    this.setState({
      currentEndpoint: this.endpointModels[0]
    })
  };

  /**
   * Load endpoint details from preset
   * @param {Object} preset
   */
  onCopyFromPreset = preset => {
    const currentEndpoint: ConnectionRestEndpoint = this.state.currentEndpoint;
    currentEndpoint.applyPreset(preset);
  };

  /**
   * Get new endpoint default name
   */
  getNewEndpointName = () => {
    const { endpointModels } = this;
    const endpointCount = endpointModels.length;

    const checkExists = (name) => {
      return some(endpoint => endpoint.endpointName === name, endpointModels);
    };

    const generateName = (index) => {
      const newName = 'Endpoint' + index;
      if (checkExists(newName)) return generateName(index + 1);
      return newName;
    };

    return generateName(endpointCount + 1);
  };

  /**
   * Add endpoint handler
   */
  @action
  onEndpointAdd = () => {
    const newEndpoint = this.createNewEndpointInstance();
    newEndpoint.endpointName = this.getNewEndpointName();
    this.endpointModels.push(newEndpoint);
    this.setState({ currentEndpoint: newEndpoint });
  };

  /**
   * Select endpoint handler
   * @param {ConnectionRestEndpoint} endpoint
   */
  @action
  onEndpointSelect = endpoint => {
    const { validation } = this.props;
    validation.reset();
    this.setState({ currentEndpoint: endpoint });
  };

  /**
   * Remove endpoint handler
   * @param {ConnectionRestEndpoint} endpoint
   * @param {Event} e click event.
   */
  @action
  onEndpointRemove = (endpoint, e) => {
    e.stopPropagation();
    this.endpointModels.remove(endpoint);
    if (this.state.currentEndpoint === endpoint) {
      this.setState({ currentEndpoint: this.endpointModels[0] });
    }
  };

  /**
   * Load endpoint presets from API
   * @returns {Promise}
   */
  loadPresets() {
    const { connectionDetailStore } = this.props;
    return new Promise((resolve, reject) => {
      connectionDetailStore
        .loadConnectionStringPreset(connectionDetailStore.item.serverTypeId)
        .then(
          result => {
            if (result.success) {
              resolve(toPairs(result.data));
            } else {
              reject(result);
            }
          },
          err => reject(err)
        );
    });
  }

  loadConnectionStringData() {
    const { connectionDetailStore, connectionDetailStore: { item } } = this.props;
    return new Promise((resolve, reject) => {
      connectionDetailStore
        .loadConnectionStringData(item)
        .then(
          result => {
            if (result.success) {
              resolve(toPairs(result.data));
            } else {
              reject(result);
            }
          },
          err => {
            reject(err);
          }
        )
    });
  }

  /**
   * Render endpoints dropdown. Those endpoints can be used as template for current.
   */
  renderPresets() {
    const { presets } = this.state;
    const unsavedPresetList = this.getUnsavedPresets();

    let presetsFlat = flatten(presets);
    if (unsavedPresetList && unsavedPresetList.length) {
      presetsFlat = [unsavedPresetList, ...presetsFlat];
    }
    let i = 0;
    const renderHeader = label => (
      <h3
        key={i++} className="dropdown-header text-bold">
        <a>{label}</a>
      </h3>
    );
    const renderPresets = presets => {
      return map(preset => (
        <li
          key={i++}
          className="padding-left">
          <a onClick={partial(this.onCopyFromPreset, [preset])}>
            {preset.endpointName}
          </a>
        </li>
      ), presets);
    };

    return <div className="btn-group pull-right">
      <Button
        disabled={!presetsFlat || !presetsFlat.length}
        className="btn-report-default dropdown-toggle"
        data-toggle="dropdown"
        label={localizer.getText('CONNECTION_STRING_BUILDER_REST_LOAD_PRESET')}>
        <span className="caret"/>
      </Button>
      <ul className="dropdown-menu">
        {map(
          item => isArray(item) ? renderPresets(item) : renderHeader(item),
          presetsFlat)}
      </ul>
    </div>;
  }

  /**
   * Render endpoint tabs
   */
  renderTabs() {
    const { endpointModels } = this;
    const { currentEndpoint } = this.state;
    const endpointCount = endpointModels.length;
    if (endpointCount === 0 || isNil(currentEndpoint))
      return null;

    const getEndpointTabClass = endpoint => {
      return classnames('endpoint-tab validator', {
        'active': endpoint === currentEndpoint,
        'errors': !!endpoint.getValidationMessage(endpointModels)
      });
    };

    const getGridCellsPositionStyle = index =>
      BrowserUtils.isIEBrowser()
        ? {
          msGridColumn: 1,
          msGridRow: index + 1
        } : {
          gridColumn: 1,
          gridRow: index + 1
        };

    const makeOnClickHandler = endpoint => partial(this.onEndpointSelect, [endpoint]);
    const makeOnRemoveHandler = endpoint => partial(this.onEndpointRemove, [endpoint]);
    let i = 0;
    return (
      <div className="connection-string-builder-tabs u_fullSize">
        {endpointModels.map((endpoint: ConnectionRestEndpoint, index) => (
          <div
            key={i++}
            className={getEndpointTabClass(endpoint)}
            onClick={makeOnClickHandler(endpoint)}
            title={endpoint.getValidationMessage(endpointModels)}
            style={getGridCellsPositionStyle(index)}>

            <div>{endpoint.endpointName || '<empty>'}</div>

            {endpointCount > 1 &&
            (
              <span
                onClick={makeOnRemoveHandler(endpoint)}
                className="endpoint-tab-remove icon-cancel"/>
            )}
          </div>
        ))}

        {/*Add endpoint tab*/}
        <div onClick={this.onEndpointAdd}
             className="endpoint-tab-add"
             style={getGridCellsPositionStyle(this.endpointModels.length)}>
          <div className="icon-plus add-endpoint-icon"/>
          <div className="add-endpoint-label" style={{msGridRow: 2}}>
            {localizer.getText('CONNECTION_STRING_BUILDER_ADD_ENDPOINT')}
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render currently selected endpoint form
   */
  renderBody() {
    const { currentEndpoint } = this.state;
    if (isNil(currentEndpoint))
      return null;
    return (
      <ConnectionStringBuilderEndpoint endpointModel={currentEndpoint} cachingConfiguration={this.cachingConfiguration} />
    );
  }

  /**
   * Render loading bar
   */
  renderLoadingBar = (): any => <LoadingComponent/>;

  render() {
    const { isLoaded } = this.state;
    return (
      <Popup visible onClosing={this.closePopup} type='none' autoClose={false} sizeClass="modal-lg" turnOffBodyDrag>
        <Popup.Body>
          <div className="connection-string-builder-rest-wrapper">

            {/*Custom header*/}
            <div className="connection-string-builder-header">
              <h4 className="modal-title u_floatLeft none">
                {localizer.getText('CONNECTION_STRING_BUILDER_REST_TITLE')}
              </h4>
              {/*Load preset dropdown*/}
              {isLoaded && this.renderPresets()}
            </div>

            <div className="connection-string-builder-body-wrapper">
              {!isLoaded && this.renderLoadingBar()}

              {/*Body*/}
              {isLoaded && (
                <ScrollBox>
                  <div className="connection-string-builder-body u_fullSize">
                    {this.renderTabs()}
                    {this.renderBody()}
                  </div>
                </ScrollBox>
              )}
            </div>
          </div>
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton
            type="accept"
            disabled={!isLoaded}>
            {localizer.getText('COMMON_LABEL_OK')}
          </Popup.ConfirmationButton>
          <Popup.ConfirmationButton
            type="cancel"
            disabled={!isLoaded}>
            {localizer.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/ConnectionStringBuilderRest/Izenda.ConnectionStringBuilderRest.js